/*
 * Copyright (c) 2024 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import {
  AccountInfo,
  AuthenticationResult,
  Configuration,
  IPublicClientApplication,
  LogLevel,
  NavigationClient,
  NavigationOptions,
  SilentRequest,
  BrowserCacheLocation
} from "@azure/msal-browser";
import {callMsGraph} from "./graph";
import * as MicrosoftGraph from "@microsoft/microsoft-graph-types"
import {NavigateFunction} from "react-router-dom";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */
export const msalConfig: Configuration = {
  auth: {

    clientId: window.location.hostname === "argos-release.p1.ultifi.azure.ext.gm.com" ? "44ff9285-9e78-43c2-8d3a-4fc417ebb8e2" : "24d7c11e-ade8-4418-bc0d-8a8ab3be08a7",
    authority: window.location.hostname === "argos-release.p1.ultifi.azure.ext.gm.com" ? "https://login.microsoftonline.com/5de110f8-2e0f-4d45-891d-bcf2218e253d" : "https://login.microsoftonline.com/6baa4eb1-106a-440b-b4f6-50134497d36d",
    redirectUri: window.location.origin
  },
  cache: {
    cacheLocation: BrowserCacheLocation.SessionStorage, // This configures where your cache will be stored
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            // console.error(message);
            return;
          case LogLevel.Info:
            // console.info(message);
            return;
          case LogLevel.Verbose:
            // console.debug(message);
            return;
          case LogLevel.Warning:
            // console.warn(message);
            return;
          default:
            return;
        }
      }
    }
  }
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest: SilentRequest = {
  forceRefresh: true,
  scopes: ["User.Read", "User.Read.All"]
};

const SEARCH_USER_KEYS = ['mail', 'givenName', 'surname', 'displayName', 'userPrincipalName']

/**
 * not case sensitive. filters for each key, space sensitive): startswith(key,searchValue). Then, if >=2 words provided,
 * removes leading and trailing spaces and checks for givenName (first name) startswith first word and surname (last name)
 * starts with second word.
 * @param keys list of keys for filter
 * @param searchValue
 */
const makeStartsWithFilterQuery = (keys: string[], searchValue: string) => {
  let query = '$filter=('
  keys.forEach((v, i) => {
    query += `startswith(${v},'${searchValue}')`
    if (i != keys.length - 1) query += ' OR '
  })
  query += ')'
  const trimmedSearchValue = searchValue.trim()
  const splitSearchValue = trimmedSearchValue.split(' ')

  /**
   * This covers the case where nothing is found in previous filters because for example the displayName is not 'first last',
   * because the previous filters check different keys for 'startswith(key,searchValue)'.
   *
   * So, it uses the last word as surname if two words provided. I put parentheses around the first query, but
   * im not sure if when it would use the first query AND NOT the second query if it found something in first query. I tried
   * 'joaquin m c' and when >=2 the results are like 'joaquin c' when ==2 or >=2, except one less joaquin (doesnt display the joaquin with givenname two words).
   * but when ==2 i can search 'joaquin m c' and just get one result because
   * joaquin's displayName is 'joaquin m carcache' so the first query finds it with startswith(displayName,'joaquin m c').
   * EDIT: now checks startswith instead of eq for givenName.
   *
   *
   * If we checked for surname eq instead of startsWith, then in cases
   * where 'displayName=last,first' or contains the middle initial, then we lose the ability to search using only
   * part of last name like 'first la'.
   */
  if (splitSearchValue.length >= 2) {
    const surnameIndex = splitSearchValue.length - 1
    const surname = splitSearchValue[surnameIndex]
    // query += ` OR (givenName eq '${splitSearchValue[0]}' AND startswith(surname, '${surname}'))`
    query += ` OR (startswith(givenName,'${splitSearchValue[0]}') AND startswith(surname, '${surname}'))`
  }
  return query
}

export const queryUsers = (accessToken: string, searchValue: string) => {
  const query = makeStartsWithFilterQuery(SEARCH_USER_KEYS, searchValue)
  const endpoint = `https://graph.microsoft.com/v1.0/users?${query}`
  const response: Promise<MicrosoftGraph.User[]> = callMsGraph(accessToken, endpoint)
  return response
}
/**
 * Add here the scopes to request when obtaining an access token for MS Graph API. For more information, see:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/resources-and-scopes.md
 * TODO: 1.0 vs 2.0?
 */

export const requestIdToken = async (instance: IPublicClientApplication, accounts: AccountInfo[]) => {
  //console.log("common accesstoken code")
  const request = {
    ...loginRequest,
    account: accounts[0]
  };
  let idTok = ""
  // const urlIsLocal = process.env.REACT_APP_SERVER_URL?.includes('localhost')
  // const shouldBypassAuthEnvVar = process.env.REACT_APP_LOCAL_BYPASS_AUTH === 'true'
  // const nodeEnvIsDev = process.env.NODE_ENV === 'development'
  // const shouldBypassAuth = urlIsLocal && shouldBypassAuthEnvVar && nodeEnvIsDev
  // if(shouldBypassAuth){
  //   console.warn('Bypassing authentication for local development')
  //   return ''
  // }
  // Silently acquires an access token which is then attached to a request
  await instance.acquireTokenSilent(request).then((response) => {
    //console.log(response.idToken)
    idTok = response.idToken
    // callMsGraph(response.accessToken)
    //setIdToken(response.idToken);
  }).catch(async (e) => {
    console.log('authConfig.ts: requestIdToken: acquireTokenSilent error: ', e)
    await instance.handleRedirectPromise();

    await instance.acquireTokenRedirect(request)/*.then((response) => {
          idTok=response.idToken
         // setIdToken(response.idToken);
        });*/
  });
  return idTok;
}

export const acquireToken = async (instance: IPublicClientApplication, accounts: AccountInfo[]): Promise<AuthenticationResult | null> => {
  const request = {
    ...loginRequest,
    account: accounts[0]
  };
  // Silently acquires an access token which is then attached to a request
  let _response: AuthenticationResult | null = null
  await instance.acquireTokenSilent(request).then((response) => {
    _response = response
    return response
  }).catch(async (e) => {
    console.log('authConfig.ts: acquireToken: acquireTokenSilent error: ', e, instance)
    await instance.acquireTokenRedirect(request)
  });
  return _response;
}

export const checkResponse = (instance: IPublicClientApplication, response: Response | XMLHttpRequest | any, shouldReturn = true,
                              shouldThrowError = true) => {
  if (response.status === 401) {
    //console.log(response.status);
    // instance.loginRedirect(loginRequest).catch(e => {
    //   console.log('authConfig.ts: checkResponse: loginRedirect error: ', e, instance)
    // })
  }
  else if (response.status !== 200 && response.status !== 202 && shouldThrowError) {
    throw new Error(response.statusText)
  } else if (shouldReturn) {
    return response.json();
  }
}

/**
 * This is an example for overriding the default function MSAL uses to navigate to other urls in your webpage
 */
export class CustomNavigationClient extends NavigationClient {
  private navigate: NavigateFunction;

  constructor(navigate: NavigateFunction) {
    super();
    this.navigate = navigate;
  }

  /**
   * Navigates to other pages within the same web application
   * You can use the useNavigate hook provided by react-router-dom to take advantage of client-side routing
   * @param url
   * @param options
   */
  async navigateInternal(url: string, options: NavigationOptions) {
    const relativePath = url.replace(window.location.origin, "");
    if (options.noHistory) {
      this.navigate(relativePath, {replace: true});
    } else {
      this.navigate(relativePath);
    }

    return false;
  }
}
  
